/**
 * Phase 4B-1 — the customer's own bot, as query hooks.
 *
 * Link status POLLS while a connection is in progress: Pocket Option login runs
 * asynchronously in the bot (launch Chromium, sign in, possibly get challenged),
 * so the answer arrives seconds after the request that started it. Polling stops
 * once the state settles, so a linked customer is not hitting the server forever.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type LinkStatus, type TenantStatus } from '@/lib/api';

export const tenantKeys = {
  status: ['tenant', 'status'] as const,
  link: ['tenant', 'link'] as const,
};

async function fetchTenantStatus(): Promise<TenantStatus | null> {
  try {
    return await api.tenantStatus();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function useTenantStatus() {
  return useQuery({ queryKey: tenantKeys.status, queryFn: fetchTenantStatus, retry: false });
}

async function fetchLinkStatus(): Promise<LinkStatus | null> {
  try {
    return await api.linkStatus();
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 503)) return null;
    throw e;
  }
}

export function useLinkStatus() {
  return useQuery({
    queryKey: tenantKeys.link,
    queryFn: fetchLinkStatus,
    retry: false,
    // Only poll while the outcome is still moving. 'linking' resolves on its
    // own; every other state needs either the customer or an operator, so
    // polling would just be noise.
    refetchInterval: (q) => (q.state.data?.state === 'linking' ? 3000 : false),
  });
}

export function useProvisionBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.provisionBot,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantKeys.status });
      void qc.invalidateQueries({ queryKey: tenantKeys.link });
    },
  });
}

export function useLinkAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.linkAccount,
    // Write the returned state straight into the cache so the UI switches to
    // "connecting" immediately rather than waiting for the next poll.
    onSuccess: (r) => qc.setQueryData(tenantKeys.link, r),
  });
}

export function useUnlinkAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.unlinkAccount,
    onSuccess: (r) => {
      qc.setQueryData(tenantKeys.link, r);
      void qc.invalidateQueries({ queryKey: tenantKeys.status });
    },
  });
}
